import { useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Flag } from "lucide-react";

const CATEGORIES = [
  { value: "spam", label: "Spam" },
  { value: "inappropriate", label: "Inappropriate content" },
  { value: "fraud", label: "Fraud or scam" },
  { value: "no_show", label: "No-show" },
  { value: "safety", label: "Safety concern" },
  { value: "other", label: "Other" },
];

/**
 * Reusable report dialog.
 *
 * props:
 *  - targetType: 'vendor' | 'user'
 *  - targetId:   vendor_id or user_id being reported
 *  - targetLabel: display name of the target ("Chef Alma", "Sarah Malhotra")
 *  - bookingId:  optional interaction reference to attach
 *  - trigger:    optional custom trigger element; falls back to a compact link
 */
export default function ReportDialog({ targetType, targetId, targetLabel = "", bookingId = null, trigger, className = "" }) {
  const [open, setOpen] = useState(false);
  const [category, setCategory] = useState("");
  const [description, setDescription] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const submit = async () => {
    if (!category) return toast.error("Choose a category");
    if (description.trim().length < 10) return toast.error("Please add at least 10 characters of detail");
    setSubmitting(true);
    try {
      await api.post("/reports", {
        target_type: targetType,
        target_id: targetId,
        category,
        description: description.trim(),
        booking_id: bookingId,
      });
      toast.success("Report submitted. Our team will review it shortly.");
      setOpen(false);
      setCategory("");
      setDescription("");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Could not file report");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      {trigger ? (
        <span onClick={() => setOpen(true)}>{trigger}</span>
      ) : (
        <button
          data-testid="open-report-dialog"
          onClick={() => setOpen(true)}
          className={`inline-flex items-center gap-1.5 text-xs text-[#5C5C5C] hover:text-[#C05A46] transition-colors ${className}`}
        >
          <Flag className="w-3.5 h-3.5" /> Report
        </button>
      )}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="report-dialog" className="max-w-md bg-[#FDFBF7]">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl">
              Report {targetType === "vendor" ? "vendor" : "planner"}
            </DialogTitle>
            <DialogDescription className="text-xs text-[#5C5C5C]">
              {targetLabel ? `Filing a report about ${targetLabel}. ` : ""}
              Our trust &amp; safety team reviews every report. We keep your identity confidential from the reported party.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            <div>
              <label className="eyebrow mb-2 block">Category</label>
              <Select value={category} onValueChange={setCategory}>
                <SelectTrigger data-testid="report-category-select" className="bg-white h-11">
                  <SelectValue placeholder="Choose a category" />
                </SelectTrigger>
                <SelectContent>
                  {CATEGORIES.map(c => (
                    <SelectItem key={c.value} value={c.value} data-testid={`report-category-${c.value}`}>
                      {c.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="eyebrow mb-2 block">What happened?</label>
              <Textarea
                data-testid="report-description-input"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Share as much detail as you can. Include dates, message excerpts, and any specifics that help us investigate."
                className="bg-white min-h-[140px]"
                maxLength={2000}
              />
              <div className="text-[11px] text-[#5C5C5C] mt-1">{description.length} / 2000</div>
            </div>
          </div>
          <DialogFooter className="mt-2">
            <Button
              variant="outline"
              onClick={() => setOpen(false)}
              disabled={submitting}
              className="rounded-full"
              data-testid="report-cancel-btn"
            >Cancel</Button>
            <Button
              onClick={submit}
              disabled={submitting}
              className="btn-sage rounded-full"
              data-testid="report-submit-btn"
            >{submitting ? "Submitting…" : "Submit report"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
